import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from "@angular/core";
import { Bot } from "../../../models/bot";
import { ActionRow } from "../../../models/action-row";
import { BaseError } from "../../pages/base-error";
import { ToastrService } from "ngx-toastr";
import { MuleService } from "../../../models/mule-service";
import { MuleControllerService } from "../../../services/mule-controller/mule-controller.service";
import { BotControllerService } from "../../../services/role-controller/bot-controller.service";
import { map } from "rxjs/operators";
import Helpers from "../../../utils/helpers";

@Component({
    selector: "action-builder",
    templateUrl: "./action-builder.component.html",
    styleUrls: ["./action-builder.component.scss"],
})
export class ActionBuilderComponent extends BaseError implements OnInit, OnChanges {

    @Input() public serviceBots: Bot[] = [];
    @Input() public tmpAction?: ActionRow;
    @Input() public stickyScrolling: boolean = false;

    @Output() public saveAction: EventEmitter<any> = new EventEmitter();
    @Output() public updateAction: EventEmitter<any> = new EventEmitter();
    @Output() public cancelEdition: EventEmitter<any> = new EventEmitter();
    @Output() public openNewModal: EventEmitter<any> = new EventEmitter();
    @Output() public openEditionModal: EventEmitter<any> = new EventEmitter();
    @Output() public deleteBot: EventEmitter<any> = new EventEmitter();

    // Search
    public searchValue: string = "";
    private updateSearchSender: any;

    // Type filter
    public currentBotType: string = "ALL";
    public availableBotTypes: Array<{ id: string, name: string, icon: string }> = [
        { id: "ALL", name: "All", icon: "globe" },
        { id: "data", name: "Data", icon: "database" },
        { id: "document", name: "Document", icon: "file" },
        { id: "internal", name: "Internal", icon: "network-wired" },
        { id: "message", name: "Message", icon: "envelope" },
        { id: "link", name: "Link", icon: "link-simple" },
        { id: "system", name: "System", icon: "cog" },
    ];

    // Filtered list
    public filteredBots: Bot[] = [];

    // Loading state
    public isReady: boolean = false;
    public muleServices: MuleService[] = [];

    constructor(
        toastr: ToastrService,
        private muleService: MuleControllerService,
        private botController: BotControllerService,
    ) {
        super(toastr);
    }

    public ngOnInit(): void {
        this.loadMuleServices();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.serviceBots) {
            // Load internal bot mappings
            this.loadInternalBotMappings();
            // If mule services are already loaded, update bot status
            if (this.muleServices.length) {
                this.updateDataBotStatuses();
            }
            this.applyFilters();
        }
    }

    private loadInternalBotMappings(): void {
        const promises = [];
        this.serviceBots?.filter((b) => {
            return b.botType === "internal";
        }).forEach((b) => {
            promises.push(
                this.botController.getBot(b.id).pipe(map((fullbot) => {
                    return fullbot;
                })).toPromise(),
            );
        });
        Promise.all<any[]>(promises).then((results: any[]) => {
            this.serviceBots?.filter((b) => {
                return b.botType === "internal";
            }).forEach((b) => {
                const newB = results.find((n) => n.id === b.id);
                if (newB) {
                    b.inputFieldsMapping = newB.inputFieldsMapping;
                    b.outputFieldsMapping = newB.outputFieldsMapping;
                }
            });
        });
    }

    private updateDataBotStatuses(): void {
        this.serviceBots?.filter((b) => {
            return b.botType === "data";
        }).forEach((bot) => {
            const existingDB = this.muleServices.find((d) => {
                return d.id === bot.botServiceId;
            });
            if (!existingDB) {
                bot.botServiceId = null;
            } else {
                bot.latestBotServiceId = this.checkVersion(bot);
            }
        });
    }

    // Search with debounce
    public searchTextChanged(value: string): void {
        this.searchValue = value;
        clearTimeout(this.updateSearchSender);
        this.updateSearchSender = setTimeout(() => {
            this.applyFilters();
        }, 300);
    }

    public resetListSearch(): void {
        this.searchValue = "";
        this.applyFilters();
    }

    // Type filter (single-select)
    public filterBotType(typeId: string): void {
        this.currentBotType = typeId;
        this.applyFilters();
    }

    // Apply both search and type filters
    public applyFilters(): void {
        let result = this.serviceBots || [];

        // Type filter
        if (this.currentBotType !== "ALL") {
            result = result.filter(bot => bot.botType === this.currentBotType);
        }

        // Search filter (case-insensitive name match)
        if (this.searchValue) {
            const search = this.searchValue.toLowerCase();
            result = result.filter(bot => bot.name?.toLowerCase().includes(search));
        }

        this.filteredBots = result;
    }

    // Icon getter for bot type
    public getBotIcon(bot: Bot): string {
        const icons = {
            data: "database",
            document: "file",
            internal: "network-wired",
            message: "envelope",
            link: bot.isEregistrationService ? "circle-nodes" : "link-simple",
            system: "cog"
        };
        return icons[bot.botType] || "robot";
    }

    // Status methods (from bot-list)
    public getBotStatusClass(bot: Bot): string {
        switch (bot.botType) {
            case "internal":
                return this.getInternalStatus(bot);
            case "message":
                return this.getMessageStatus(bot);
            case "data":
                return this.getDataStatus(bot);
            default:
                return "";
        }
    }

    public getBotTooltip(bot: Bot): string {
        const typeKey = `bots.${bot.botType}Bot`;
        switch (bot.botType) {
            case "internal":
                return `${typeKey} - ${this.getInternalStatusTooltip(bot)}`;
            case "message":
                return `${typeKey} - ${this.getMessageStatusTooltip(bot)}`;
            case "data":
                return `${typeKey} - ${this.getDataStatusTooltip(bot)}`;
            case "link":
                return bot.redirectUrl || "Link Bot";
            default:
                return typeKey;
        }
    }

    public getInternalStatus(bot: Bot): string {
        return bot.inputFieldsMapping?.length ? "bg-success text-light" : "bg-warning text-light";
    }

    public getInternalStatusTooltip(bot: Bot): string {
        return bot.inputFieldsMapping?.length ? "Mapping started" : "Mapping is empty";
    }

    public getMessageStatus(bot: Bot): string {
        return bot.selectedMessage && bot.selectedReceiver ? "bg-success text-light" : "bg-warning text-light";
    }

    public getMessageStatusTooltip(bot: Bot): string {
        return bot.selectedMessage && bot.selectedReceiver ? "Message Bot is ready" : "Message Bot is not complete";
    }

    public getDataStatus(bot: Bot): string {
        if (!bot.botServiceId) {
            return "bg-warning text-light";
        }
        return bot.latestBotServiceId ? "bg-success text-light" : "bg-danger text-light";
    }

    public getDataStatusTooltip(bot: Bot): string {
        if (!bot.botServiceId) {
            return "No service selected";
        }
        return bot.latestBotServiceId ? "Latest service" : "Newer service available";
    }

    // Mule services loading (from bot-list)
    public loadMuleServices(): void {
        this.muleService.getServicesList().subscribe(
            (data) => {
                this.isReady = true;
                this.muleServices = data;
                this.serviceBots?.filter((b) => {
                    return b.botType === "data";
                }).forEach((bot) => {
                    const existingDB = this.muleServices.find((d) => {
                        return d.id === bot.botServiceId;
                    });
                    if (!existingDB) {
                        bot.botServiceId = null;
                    } else {
                        bot.latestBotServiceId = this.checkVersion(bot);
                    }
                });
                this.applyFilters();
            },
            (err) => {
                this.isReady = true;
                this.showErrorMessage(err);
                this.applyFilters();
            },
        );
    }

    public checkVersion(bot: Bot): boolean {
      if (!bot.botServiceId) {
        return false;
      }
      const serviceNamePrefix = Helpers.getBotServiceNamePrefix(bot.botServiceId);
      const serviceVersion = Helpers.getBotServiceVersion(bot.botServiceId);
      for (const muleService of this.muleServices) {
        if (Helpers.getBotServiceNamePrefix(muleService.id) === serviceNamePrefix) {
          const version = Helpers.getBotServiceVersion(muleService.id);
          if (version > serviceVersion) {
            return false;
          }
        }
      }
      return true;
    }

    // Existing methods
    public saveActionComponent(): void {
        this.saveAction.emit(true);
    }

    public cancelEdit(): void {
        this.cancelEdition.emit(true);
    }

    public updateBots(e: any): void {
        this.updateAction.emit(e);
    }

    public openAddModal(): void {
        this.openNewModal.emit(true);
    }

    public openEditModal(e: any): void {
        this.openEditionModal.emit(e);
    }

    public confirmDeleteBot(bot: Bot): void {
        this.deleteBot.emit(bot);
    }

    public selectBot(bot: Bot): void {
        this.updateAction.emit({ bot, add: true });
        this.saveAction.emit(true);
    }

    public isBotSelected(bot: Bot): boolean {
        if (!this.tmpAction || !this.tmpAction.bots) {
            return false;
        }
        return this.tmpAction.bots.some(b => b.id === bot.id);
    }

}
